import React, { useContext, useMemo, useState, useCallback } from 'react';
import { getLocale } from 'umi';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import MetricsCardWrap from '@/pages/dashboard/components/MetricsCardWrap';
import MetricsCardValue from '@/pages/dashboard/components/MetricsCardValue';
import CircularProgress from '@/pages/dashboard/components/CircularProgress';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import CommonBubble from '@home/components/CommonBubble';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import {
  getMetricValue,
  calcPercent,
  getDateRangeText,
  getMonthTargetLabel,
  resolveDataKey,
  isFilterOverridesMatched,
} from '@/pages/dashboard/utils/kpiCardUtils';
import type {
  IKpiProgressDetailCardProps,
  IKpiTableColumn,
  IKpiTableRow,
  ISortState,
  TKpiDetailSummaryTargetModule,
} from './types';
import './index.scss';

const DEFAULT_LOADING_HEIGHT = 548;
const DEFAULT_BAR_WIDTH = 110;
const DEFAULT_PERCENT_DECIMAL = 2;
const DEFAULT_COLOR = '#326ff0';
const DEFAULT_SUCCESS_COLOR = '#69d19c';
const SORT_DEFAULT = '#262832';
const SORT_ACTIVE = '#326ff0';

const KpiProgressDetailCard: React.FC<IKpiProgressDetailCardProps> = (props) => {
  const mergedProps = { ...props, ...(props.config?.props || {}) };
  const {
    data,
    className,
    style,
    titleCn,
    titleEn,
    icon,
    summaryValueMetric,
    summaryTargetMetric,
    summaryValueMetrics,
    summaryTargetMetrics,
    summaryPercentMetric,
    formatRule = 'thousandsInit',
    preUnit,
    tableColumns = [],
    tableRows = [],
    targetLabelCn,
    targetLabelEn,
    subtitleCn,
    subtitleEn,
    dataKey: explicitDataKey,
    summaryValueDataKey: explicitSummaryValueDataKey,
    summaryTargetDataKey: explicitSummaryTargetDataKey,
    loadingHeight = DEFAULT_LOADING_HEIGHT,
    progressColor = DEFAULT_COLOR,
    progressSuccessColor = DEFAULT_SUCCESS_COLOR,
    barWidth = DEFAULT_BAR_WIDTH,
    percentDecimal = DEFAULT_PERCENT_DECIMAL,
    targetProgressFilterOverrides = [],
    hiddenTableColumnCodes = [],
    hiddenTableColumnCodesWhenFilterMismatch,
    hiddenTargetModules = [],
    hiddenTargetModulesWhenFilterMismatch,
  } = mergedProps;

  const isEN = getLocale() === LANGUAGE_EN;
  const { filterVal } = useContext(FilterContext);

  const [sortState, setSortState] = useState<ISortState>({ key: '', order: null });

  const dataKey = useMemo(() => resolveDataKey(data, explicitDataKey), [data, explicitDataKey]);
  const summaryValueDataKey = useMemo(
    () => resolveDataKey(data, explicitSummaryValueDataKey || explicitDataKey),
    [data, explicitDataKey, explicitSummaryValueDataKey],
  );
  const summaryTargetDataKey = useMemo(
    () => resolveDataKey(data, explicitSummaryTargetDataKey || explicitDataKey),
    [data, explicitDataKey, explicitSummaryTargetDataKey],
  );

  const metricsMap = data?.[dataKey]?.data;
  const summaryValueMetricsMap = data?.[summaryValueDataKey]?.data;
  const summaryTargetMetricsMap = data?.[summaryTargetDataKey]?.data;
  const loading = Boolean(
    data?.[dataKey]?.loading
    || data?.[summaryValueDataKey]?.loading
    || data?.[summaryTargetDataKey]?.loading,
  );
  const errorMsg = data?.[dataKey]?.errorMsg
    || data?.[summaryValueDataKey]?.errorMsg
    || data?.[summaryTargetDataKey]?.errorMsg;

  const actualValue = useMemo(() => {
    if (summaryValueMetrics?.length && summaryValueMetricsMap) {
      const sum = summaryValueMetrics.reduce((acc: number, m: string) => {
        const v = parseFloat(getMetricValue(summaryValueMetricsMap, m));
        return acc + (Number.isNaN(v) ? 0 : v);
      }, 0);
      return String(sum);
    }
    return getMetricValue(summaryValueMetricsMap || metricsMap, summaryValueMetric);
  }, [metricsMap, summaryValueMetric, summaryValueMetrics, summaryValueMetricsMap]);

  const targetValue = useMemo(() => {
    if (summaryTargetMetrics?.length && summaryTargetMetricsMap) {
      const sum = summaryTargetMetrics.reduce((acc: number, m: string) => {
        const v = parseFloat(getMetricValue(summaryTargetMetricsMap, m));
        return acc + (Number.isNaN(v) ? 0 : v);
      }, 0);
      return String(sum);
    }
    return getMetricValue(summaryTargetMetricsMap || metricsMap, summaryTargetMetric);
  }, [metricsMap, summaryTargetMetric, summaryTargetMetrics, summaryTargetMetricsMap]);
  const percent = summaryPercentMetric
    ? parseFloat(getMetricValue(metricsMap, summaryPercentMetric)) || 0
    : calcPercent(actualValue, targetValue);

  const subtitleText = useMemo(() => {
    const staticText = getName(isEN, subtitleEn, subtitleCn);
    return staticText || getDateRangeText(filterVal);
  }, [filterVal, isEN, subtitleCn, subtitleEn]);

  const monthTargetLabel = useMemo(
    () => getMonthTargetLabel(filterVal, isEN, targetLabelCn, targetLabelEn),
    [filterVal, isEN, targetLabelCn, targetLabelEn],
  );
  const filterOverridesMatched = useMemo(() => {
    if (targetProgressFilterOverrides.length === 0) return true;
    return isFilterOverridesMatched(filterVal, targetProgressFilterOverrides);
  }, [filterVal, targetProgressFilterOverrides]);

  const mismatchHiddenColumns = useMemo(
    () => hiddenTableColumnCodesWhenFilterMismatch ?? [],
    [hiddenTableColumnCodesWhenFilterMismatch],
  );

  const mismatchHiddenSummaryModules = useMemo(
    () => hiddenTargetModulesWhenFilterMismatch ?? [],
    [hiddenTargetModulesWhenFilterMismatch],
  );

  const isColumnHidden = useCallback(
    (code: IKpiTableColumn['code']): boolean => {
      if (hiddenTableColumnCodes.includes(code)) return true;
      if (
        !filterOverridesMatched
        && mismatchHiddenColumns.includes(code)
      ) {
        return true;
      }
      return false;
    },
    [
      hiddenTableColumnCodes,
      filterOverridesMatched,
      mismatchHiddenColumns,
    ],
  );

  const isSummaryModuleHidden = useCallback(
    (key: TKpiDetailSummaryTargetModule): boolean => {
      if (hiddenTargetModules.includes(key)) return true;
      if (
        !filterOverridesMatched
        && mismatchHiddenSummaryModules.includes(key)
      ) {
        return true;
      }
      return false;
    },
    [
      hiddenTargetModules,
      filterOverridesMatched,
      mismatchHiddenSummaryModules,
    ],
  );

  const visibleTableColumns = useMemo(() => (
    tableColumns.filter((col: IKpiTableColumn) => !isColumnHidden(col.code))
  ), [tableColumns, isColumnHidden]);

  const sumMetrics = useCallback((metrics: string[]): string => {
    if (!metricsMap || !metrics?.length) return '';
    const sum = metrics.reduce((acc, m) => {
      const v = parseFloat(getMetricValue(metricsMap, m));
      return acc + (Number.isNaN(v) ? 0 : v);
    }, 0);
    return String(sum);
  }, [metricsMap]);

  const rows = useMemo(() => {
    if (!tableRows?.length || !metricsMap) return [];
    const list = tableRows.map((row) => {
      const agg = row.aggregateFrom;
      const val = agg?.valueMetrics?.length
        ? sumMetrics(agg.valueMetrics)
        : getMetricValue(metricsMap, row.valueMetric);
      const tgt = agg?.targetMetrics?.length
        ? sumMetrics(agg.targetMetrics)
        : getMetricValue(metricsMap, row.targetMetric);
      const pct = row.percentMetric
        ? parseFloat(getMetricValue(metricsMap, row.percentMetric)) || 0
        : calcPercent(val, tgt);
      return { ...row, value: val, target: tgt, percent: pct };
    });
    const canUseSort = visibleTableColumns.some(
      (col: IKpiTableColumn) => col.code === sortState.key,
    );
    if (canUseSort && sortState.key && sortState.order) {
      // sort 回调内 a/b 为 list.map 的返回值类型：IKpiTableRow 扩展 value/target/percent 字段
      list.sort((a, b) => {
        let va: number;
        let vb: number;
        if (sortState.key === 'value') {
          va = parseFloat(a.value) || 0;
          vb = parseFloat(b.value) || 0;
        } else if (sortState.key === 'target') {
          va = parseFloat(a.target) || 0;
          vb = parseFloat(b.target) || 0;
        } else {
          va = a.percent;
          vb = b.percent;
        }
        return sortState.order === 'asc' ? va - vb : vb - va;
      });
    }
    return list;
  }, [tableRows, metricsMap, sortState, visibleTableColumns]);

  const handleSort = useCallback((key: string): void => {
    setSortState((prev) => {
      if (prev.key === key) {
        let nextOrder: 'asc' | 'desc' | null = 'asc';
        if (prev.order === 'asc') nextOrder = 'desc';
        else if (prev.order === 'desc') nextOrder = null;
        return { key: nextOrder ? key : '', order: nextOrder };
      }
      return { key, order: 'asc' };
    });
  }, []);

  const getColName = (col: IKpiTableColumn): string => getName(isEN, col.nameEn, col.nameCn) || '';

  const getColTips = (col: IKpiTableColumn): string => {
    const raw = getName(isEN, col.tipsEn, col.tipsCn) || '';
    return raw.replaceAll('\\n', '<br/>').replaceAll('\n', '<br/>');
  };

  const renderHeaderCell = (col: IKpiTableColumn): React.ReactNode => {
    const colName = getColName(col);
    const tips = getColTips(col);

    if (col.code === 'category') {
      return (
        <div className="dbd-kpi-detail-card__th dbd-kpi-detail-card__th--category">
          <span>{colName}</span>
          {tips && (
            <CommonBubble dark placement="top" content={<FormatHtmlTextToReact text={tips} />}>
              <span className="dbd-kpi-detail-card__tips-wrap">
                <SvgIcon type="alert-circle-m" className="dbd-kpi-detail-card__tips-icon" />
              </span>
            </CommonBubble>
          )}
        </div>
      );
    }

    const isSortable = col.sortable !== false;
    const isActive = sortState.key === col.code && sortState.order !== null;
    return (
      <div
        className={`dbd-kpi-detail-card__th dbd-kpi-detail-card__th--sortable${col.code === 'progress' ? ' dbd-kpi-detail-card__th--progress' : ''}${isActive ? ' dbd-kpi-detail-card__th--active' : ''}`}
        onClick={isSortable ? () => handleSort(col.code) : undefined}
        style={!isSortable ? { cursor: 'default' } : undefined}
      >
        <span>{colName}</span>
        {isSortable && <SortIcon sortKey={col.code} sortState={sortState} />}
      </div>
    );
  };

  // renderBodyCell 的 row 来自 rows useMemo 的映射结果，扩展了 value/target/percent 三个字段
  const renderBodyCell = (
    col: IKpiTableColumn,
    row: IKpiTableRow & { value: string; target: string; percent: number; },
  ): React.ReactNode => {
    const colFormat = col.formatRule || formatRule;
    const colPreUnit = col.preUnit || preUnit;

    switch (col.code) {
      case 'category':
        return (
          <div className="dbd-kpi-detail-card__td dbd-kpi-detail-card__td--category">
            {getName(isEN, row.categoryEn, row.categoryCn)}
          </div>
        );
      case 'value':
        return (
          <div className="dbd-kpi-detail-card__td dbd-kpi-detail-card__td--num">
            <MetricsCardValue value={row.value} formatRule={colFormat} preUnit={colPreUnit} emptyValue="-" className="dbd-kpi-detail-card__cell-val" />
          </div>
        );
      case 'target':
        return (
          <div className="dbd-kpi-detail-card__td dbd-kpi-detail-card__td--num">
            <MetricsCardValue value={row.target} formatRule={colFormat} preUnit={colPreUnit} emptyValue="-" className="dbd-kpi-detail-card__cell-val" />
          </div>
        );
      case 'progress': {
        const barW = Math.min(row.percent, 100) / 100 * barWidth;
        const barColor = row.percent >= 100 ? progressSuccessColor : progressColor;
        return (
          <div className="dbd-kpi-detail-card__td dbd-kpi-detail-card__td--progress">
            <span className="dbd-kpi-detail-card__pct">
              {row.percent > 0 ? `${row.percent.toFixed(percentDecimal)}%` : '-'}
            </span>
            <div className="dbd-kpi-detail-card__bar" style={{ width: barWidth }}>
              <div className="dbd-kpi-detail-card__bar-bg" />
              {row.percent > 0 && (
                <div
                  className="dbd-kpi-detail-card__bar-fill"
                  style={{ width: barW, backgroundColor: barColor }}
                />
              )}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const bodyContent = (
    <div className="dbd-kpi-detail-card">
      <div className="dbd-kpi-detail-card__summary">
        <div className="dbd-kpi-detail-card__left">
          <MetricsCardValue
            value={actualValue}
            formatRule={formatRule}
            preUnit={preUnit}
            emptyValue="-"
          />
          {!isSummaryModuleHidden('summaryTargetRow') && (
            <div className="dbd-kpi-detail-card__row">
              <span className="dbd-kpi-detail-card__label">
                {monthTargetLabel}
              </span>
              <MetricsCardValue
                className="dbd-kpi-detail-card__target-val"
                value={targetValue}
                formatRule={formatRule}
                preUnit={preUnit}
                emptyValue="-"
              />
            </div>
          )}
        </div>
        {!isSummaryModuleHidden('summaryCircle') && (
          <div className="dbd-kpi-detail-card__circle">
            <CircularProgress
              percent={percent}
              color={progressColor}
              successColor={progressSuccessColor}
            />
          </div>
        )}
      </div>

      <div className="dbd-kpi-detail-card__table">
        <div className="dbd-kpi-detail-card__thead">
          {visibleTableColumns.map((col: IKpiTableColumn) => (
            <React.Fragment key={col.code}>
              {renderHeaderCell(col)}
            </React.Fragment>
          ))}
        </div>
        {rows.map((row) => (
          <div className="dbd-kpi-detail-card__tr" key={row.valueMetric}>
            {visibleTableColumns.map((col: IKpiTableColumn) => (
              <React.Fragment key={col.code}>
                {renderBodyCell(col, row)}
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  const card = (
    <MetricsCardWrap
      titleCn={titleCn}
      titleEn={titleEn}
      icon={icon}
      className={`dbd-kpi-card dbd-kpi-card--detail ${className || ''}`}
      style={style}
      contentHeader={subtitleText ? <div className="dbd-kpi-card__date">{subtitleText}</div> : undefined}
    >
      {bodyContent}
    </MetricsCardWrap>
  );

  return (
    <Abnormal
      isLoading={loading}
      isError={!!errorMsg}
      className="dbd-kpi-card-loading dbd-kpi-card-loading--detail"
      style={{ height: loadingHeight }}
      component={card}
    />
  );
};

const SortIcon: React.FC<{ sortKey: string; sortState: ISortState; }> = ({ sortKey, sortState }) => {
  const isActive = sortState.key === sortKey;
  const upColor = isActive && sortState.order === 'asc' ? SORT_ACTIVE : SORT_DEFAULT;
  const downColor = isActive && sortState.order === 'desc' ? SORT_ACTIVE : SORT_DEFAULT;
  return (
    <span className="dbd-kpi-detail-card__sort-icon">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4.5 6.5L8 3L11.5 6.5" stroke={upColor} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.5 9.5L8 13L11.5 9.5" stroke={downColor} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
};

export default KpiProgressDetailCard;
